export type CourseLevel = "Intermediate" | "Advanced";
export type CertName = "OASP" | "OAWSP" | "OASE";

type PromoConfig = { code: string; discount: string; expiresAt: Date };
// Set _PROMO_CONFIG to activate a promo; it auto-expires at expiresAt with no manual cleanup needed.
const _PROMO_CONFIG = {
  code: "VEGAS26",
  discount: "25%",
  expiresAt: new Date("2026-08-11T00:00:00+03:00"), // midnight Nicosia time (EEST), 14 days from 2026-07-28
} as PromoConfig | null;
export const ACTIVE_PROMO: PromoConfig | null =
  _PROMO_CONFIG && Date.now() < _PROMO_CONFIG.expiresAt.getTime() ? _PROMO_CONFIG : null;

/** Public exam report template shared by all certifications. */
export const EXAM_REPORT_TEMPLATE_URL =
  "https://docs.google.com/document/d/1ZKCK1j4sA6aWQNMOxhmTY0493vfM18ls/";

export interface CourseScenario {
  /** The fictional target organisation the labs are built around. */
  client: string;
  /** Path (under /public) to the client's logo, shown in the dossier card. */
  logo: string;
  /** One-line descriptor shown under the client name. */
  clientMeta: string;
  /** Narrative paragraphs setting up the lab environment. */
  paragraphs: string[];
  /** The single-sentence lab objective, called out on its own. */
  objective: string;
}

export interface FAQItem {
  question: string;
  answer: string;
  /** Optional link rendered after the answer text. */
  link?: { label: string; href: string };
}

export interface PricingFeature {
  text: string;
  included: boolean;
}

export interface PricingTier {
  name: string;
  price: string;
  badge?: string;
  featured?: boolean;
  features: PricingFeature[];
  enrollUrl: string;
  ctaLabel?: string;
}

export interface Technique {
  name: string;
  description: string;
}

export interface Module {
  title: string;
  labs: number;
  description?: string;
  sample?: boolean;
  sampleUrl?: string;
}

export interface Course {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  platform: "Azure" | "AWS";
  level: CourseLevel;
  cert: CertName;
  certFullName: string;
  labCount: number;
  moduleCount: number;
  topicCount?: number;
  servicesCount?: number;
  videoCount: number | null;
  examAttempts: number;
  lifetimeAccess: boolean;
  discord: boolean;
  techniques: Technique[];
  modules: Module[];
  outcomes: string[];
  color: string;
  accentColor: string;
  logo: string;
  pricing: PricingTier[];
  faq: FAQItem[];
  /** Optional lab scenario students work through (advanced course only). */
  scenario?: CourseScenario;
}

export const courses: Course[] = [
  {
    id: "breaching-azure",
    slug: "breaching-azure",
    name: "Breaching Azure",
    shortName: "Breaching Azure",
    tagline: "Master offensive Azure security from the attacker's perspective.",
    description:
      "A hands-on lab course covering real-world Azure attack paths. Learn how attackers enumerate Entra ID, abuse OAuth flows, exploit Managed Identities, and escalate privileges across Azure tenants.",
    platform: "Azure",
    level: "Intermediate",
    cert: "OASP",
    certFullName: "Offensive Azure Security Professional",
    labCount: 16,
    moduleCount: 17,
    topicCount: 32,
    servicesCount: 23,
    videoCount: 9,
    examAttempts: 1,
    lifetimeAccess: true,
    discord: true,
    color: "#0078D4",
    accentColor: "rgba(0,120,212,0.15)",
    logo: "/BreachingAzure_logo.png",
    pricing: [
      {
        name: "Breaching Azure (Essential)",
        price: "$499",
        enrollUrl: "https://buy.stripe.com/28oeXs4de1oHaqccMT",
        features: [
          { text: "30 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OASP Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OASP Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
          { text: "BA+ Standalone Challenges", included: false },
        ],
      },
      {
        name: "Breaching Azure (Extended)",
        price: "$699",
        enrollUrl: "https://buy.stripe.com/eVq8wO27X7uz43V5umeEo0l",
        features: [
          { text: "60 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OASP Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OASP Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
          { text: "BA+ Standalone Challenges", included: false },
        ],
      },
      {
        name: "Breaching Azure+",
        price: "$1,000",
        badge: "Most Complete",
        featured: true,
        enrollUrl: "https://buy.stripe.com/7sIcPk6lm0kDeGsfZi",
        features: [
          { text: "60 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "PDF Training Guide", included: true },
          { text: "2x OASP Exam Attempts", included: true },
          { text: "Video Lessons", included: true },
          { text: "BA+ Standalone Challenges", included: true },
          { text: "Certificate of Completion", included: true },
        ],
      },
    ],
    faq: [
      {
        question: "What is the exam format?",
        answer: "The exam is practical and based on the material taught during the course. You are given access to a live Azure environment for 24 hours. The goal is to compromise the target environment and capture the final exam flag. You then have an additional 24 hours to submit a report detailing your methodology. Successfully completing the exam earns your OASP digital badge.",
      },
      {
        question: "Is there a required format for the exam report?",
        answer: "Yes. Write your report in the CloudBreach exam report template. It lays out the structure we grade against: your attack path, the tools and commands used at each step, evidence screenshots, and a risk analysis with remediation recommendations. Using the template makes sure nothing the graders look for is left out.",
        link: { label: "Open the exam report template", href: EXAM_REPORT_TEMPLATE_URL },
      },
      {
        question: "What level of expertise is required?",
        answer: "Around one year of Azure infrastructure experience is recommended. The labs include hints throughout, and CloudBreach administrators are available to guide you if you get stuck.",
      },
      {
        question: "Do I need to install any software?",
        answer: "No. To start the CloudBreach labs you only need a standard web browser. The training environments are fully hosted on Microsoft Azure cloud and remote access is provided through the browser.",
      },
      {
        question: "Can I enroll today and start later?",
        answer: "Yes. You can purchase the course today and schedule your lab start date for whenever works best for you.",
      },
      {
        question: "Can I pause the lab?",
        answer: "Pausing is not currently possible. The lab is deployed on your scheduled start date and runs continuously for 30 or 60 days depending on your plan.",
      },
      {
        question: "Can I extend my lab access?",
        answer: "Yes. Lab access can be extended at any time through the CloudBreach portal at cloudbreach.io/addons. Contact an administrator if you run into any issues.",
      },
      {
        question: "Can I get a refund?",
        answer: "We do not offer refunds for digital training courses. We encourage you to review the course description, objectives, and available previews before purchasing to make sure it fits your needs.",
      },
    ],
    techniques: [
      {
        name: "Entra ID Enumeration",
        description:
          "Enumerate users, groups, applications, and service principals without triggering alerts.",
      },
      {
        name: "OAuth 2.0 Abuse",
        description:
          "Exploit OAuth consent flows and device code phishing to compromise tokens.",
      },
      {
        name: "Managed Identity Exploitation",
        description:
          "Abuse system-assigned and user-assigned managed identities for lateral movement.",
      },
      {
        name: "Storage Account Attacks",
        description:
          "Enumerate and exfiltrate data from misconfigured Azure Storage Accounts.",
      },
      {
        name: "Key Vault Extraction",
        description:
          "Extract secrets and certificates from Azure Key Vault using over-privileged identities.",
      },
      {
        name: "RBAC Privilege Escalation",
        description:
          "Identify and exploit Role-Based Access Control misconfigurations to gain Owner-level access.",
      },
      {
        name: "Azure VM Compromise",
        description:
          "Leverage VM extensions and Run Command to execute code on Azure virtual machines.",
      },
      {
        name: "Function App Attacks",
        description:
          "Target Azure Function Apps to extract environment variables and pivot to backend services.",
      },
    ],
    modules: [
      { title: "Breaching Azure – Introduction", labs: 2, description: "Course overview, lab environment setup, and introduction to the Azure attack methodology used throughout the course." },
      { title: "BA 01 – Getting Started with Azure", labs: 2, description: "Set up your attack workstation, install the required tooling, and get familiar with the Azure CLI and portal navigation." },
      { title: "BA 02 – Azure Portal", labs: 1, description: "Use the Azure Portal to enumerate resources, review IAM assignments, and identify initial attack surface across a subscription." },
      { title: "BA 03 – Azure Cloud Shell", labs: 1, description: "Leverage Azure Cloud Shell as an attack platform with pre-authenticated access to Azure APIs and built-in tooling." },
      { title: "BA 04 – Anonymous Azure Services Enumeration", labs: 3, description: "Enumerate publicly exposed Azure services without credentials — storage accounts, web apps, and metadata endpoints.", sample: true, sampleUrl: "https://portal.cloudbreach.io/courses/breaching-azure/lessons/ba-01-anonymous-azure-services-enumeration/" },
      { title: "BA 05 – Phishing Attacks", labs: 6, description: "Execute device code phishing and OAuth consent attacks to harvest access tokens from Entra ID users without their passwords." },
      { title: "BA 06 – Abusing Azure Management and Azure Graph APIs", labs: 2, description: "Use the Azure Management and Microsoft Graph APIs to enumerate subscriptions, resources, role assignments, and escalate access." },
      { title: "BA 07 – Password Spraying Entra ID", labs: 2, description: "Conduct stealthy password spray attacks against Entra ID tenants while evading lockout and Smart Lockout protections." },
      { title: "BA 08 – Azure Access Token", labs: 4, description: "Extract, manipulate, and replay Azure access tokens to authenticate as users and service principals across Azure services." },
      { title: "BA 09 – Entra Conditional Access Policy", labs: 2, description: "Enumerate and bypass Conditional Access Policies to access protected resources from untrusted devices and locations." },
      { title: "BA 10 – Azure Logic App", labs: 2, description: "Abuse misconfigured Logic Apps to exfiltrate data, trigger automated actions, and pivot within the Azure environment." },
      { title: "BA 11 – Azure Storage Account", labs: 2, description: "Enumerate and exploit misconfigured Azure Storage Accounts to access blobs, tables, file shares, and queues." },
      { title: "BA 12 – Azure Automation Account", labs: 2, description: "Abuse Azure Automation Accounts and Runbooks to execute arbitrary code under managed identity privileges." },
      { title: "BA 13 – Microsoft Entra Connect & MSOL", labs: 4, description: "Exploit Entra Connect sync accounts and MSOL service accounts to escalate privileges and compromise on-premises Active Directory." },
      { title: "BA 14 – B2B Collaboration with Entra ID", labs: 4, description: "Exploit cross-tenant B2B guest access to enumerate resources and pivot laterally between Azure tenants." },
      { title: "BA 15 – Azure and Microsoft 365 REST APIs", labs: 2, description: "Use Azure and Microsoft 365 REST APIs to stealthily enumerate mailboxes, SharePoint sites, and Teams data." },
      { title: "BA 16 – DevSecOps Fundamentals", labs: 12, description: "Attack Azure DevOps pipelines, extract secrets from pipeline variables and Key Vault, and compromise service connections to escalate across environments." },
    ],
    outcomes: [
      "Enumerate Azure environments without generating high-fidelity alerts",
      "Abuse Entra ID identities and OAuth flows to obtain persistent access",
      "Exploit Managed Identities to pivot across Azure resources",
      "Extract secrets from Key Vaults and Storage Accounts",
      "Escalate privileges by abusing RBAC misconfigurations",
      "Complete end-to-end Azure compromise chains",
      "Write professional attack path reports for OASP certification",
    ],
  },
  {
    id: "breaching-aws",
    slug: "breaching-aws",
    name: "Breaching AWS",
    shortName: "Breaching AWS",
    tagline: "Attack AWS environments the way real threat actors do.",
    description:
      "Lab-first training on offensive AWS security. Cover IAM enumeration, SSRF to the metadata service, S3 bucket abuse, AssumeRole pivoting, Lambda exploitation, and persistence across AWS accounts.",
    platform: "AWS",
    level: "Intermediate",
    cert: "OAWSP",
    certFullName: "Offensive AWS Security Professional",
    labCount: 18,
    moduleCount: 18,
    topicCount: 25,
    servicesCount: 14,
    videoCount: 14,
    examAttempts: 1,
    lifetimeAccess: true,
    discord: true,
    color: "#FF9900",
    accentColor: "rgba(255,153,0,0.12)",
    logo: "/BreachingAWS_logo.png",
    pricing: [
      {
        name: "Breaching AWS (Essential)",
        price: "$599",
        enrollUrl: "https://buy.stripe.com/cN22aG7pqaZhaqcdQZ",
        features: [
          { text: "30 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OAWSP Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OAWSP Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
          { text: "Priority Support", included: false },
        ],
      },
      {
        name: "Breaching AWS (Extended)",
        price: "$799",
        enrollUrl: "https://buy.stripe.com/9AQcPk2564ATfKw28g",
        features: [
          { text: "60 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OAWSP Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OAWSP Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
          { text: "Priority Support", included: false },
        ],
      },
      {
        name: "Breaching AWS+",
        price: "$1,000",
        badge: "Most Complete",
        featured: true,
        enrollUrl: "https://buy.stripe.com/cN2bLg9xyaZh2XKaER",
        features: [
          { text: "60 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "PDF Training Guide", included: true },
          { text: "2x OAWSP Exam Attempts", included: true },
          { text: "Video Lessons", included: true },
          { text: "Priority Support", included: true },
          { text: "Certificate of Completion", included: true },
        ],
      },
    ],
    faq: [
      {
        question: "What is the exam format?",
        answer: "The exam is practical and based on the course material. You are given access to a live AWS environment for 24 hours with the goal of compromising the target and capturing the final flag. You then have 24 hours to submit a report. Passing earns your OAWSP digital badge.",
      },
      {
        question: "Is there a required format for the exam report?",
        answer: "Yes. Write your report in the CloudBreach exam report template. It lays out the structure we grade against: your attack path, the tools and commands used at each step, evidence screenshots, and a risk analysis with remediation recommendations. Using the template makes sure nothing the graders look for is left out.",
        link: { label: "Open the exam report template", href: EXAM_REPORT_TEMPLATE_URL },
      },
      {
        question: "Can I take the OAWSP exam without taking the course?",
        answer: "No. The OAWSP exam requires purchasing the course. If you fail your first attempt, you can purchase an exam voucher to try again.",
      },
      {
        question: "Do I need to install any software?",
        answer: "No. You only need a standard web browser. The training environments are fully hosted on cloud infrastructure and remote access is provided through the browser.",
      },
      {
        question: "Can I enroll today and start later?",
        answer: "Yes. You can purchase the course today and schedule your lab start date for whenever works best for you.",
      },
      {
        question: "Can I pause the lab?",
        answer: "Pausing is not currently possible. The lab is deployed on your scheduled start date and runs for 30 or 60 days depending on your plan.",
      },
      {
        question: "Can I extend my lab access?",
        answer: "Yes. Lab access can be extended at any time through the CloudBreach portal at cloudbreach.io/addons.",
      },
      {
        question: "Can I get a refund?",
        answer: "We do not offer refunds for digital training courses. Please review the course description and objectives before purchasing.",
      },
    ],
    techniques: [
      {
        name: "IAM Enumeration",
        description:
          "Map IAM users, roles, groups, and policies without authenticated API errors.",
      },
      {
        name: "SSRF to EC2 Metadata Service",
        description:
          "Exploit SSRF vulnerabilities to reach IMDSv1 and extract IAM credentials.",
      },
      {
        name: "S3 Bucket Misconfiguration",
        description:
          "Discover and exploit publicly accessible S3 buckets to exfiltrate sensitive data.",
      },
      {
        name: "AssumeRole Abuse",
        description:
          "Pivot across accounts and escalate privileges using misconfigured trust policies.",
      },
      {
        name: "Lambda Exploitation",
        description:
          "Extract environment variables, credentials, and pivot from Lambda execution roles.",
      },
      {
        name: "EC2 Instance Compromise",
        description:
          "Leverage instance profiles and user data to escalate from EC2 to full account access.",
      },
      {
        name: "Secrets Manager Extraction",
        description:
          "Identify and read secrets from AWS Secrets Manager using over-privileged roles.",
      },
      {
        name: "CloudTrail Evasion",
        description:
          "Understand CloudTrail logging gaps and minimize your attacker footprint.",
      },
    ],
    modules: [
      { title: "Introduction", labs: 2, description: "Course overview, AWS attack methodology, and lab environment setup including tooling and access configuration." },
      { title: "Rules of Engagement", labs: 1, description: "Ethical and legal guidelines for the course labs, scope boundaries, and responsible disclosure expectations." },
      { title: "BAWS 01 – Enumerating S3 Buckets", labs: 2, description: "Discover and access publicly exposed S3 buckets to enumerate objects, extract sensitive data, and map storage attack surface.", sample: true, sampleUrl: "https://portal.cloudbreach.io/courses/breaching-aws/lessons/step1/topics/baws-01-lab/" },
      { title: "BAWS 02 – AWS Subdomain Takeover", labs: 1, description: "Claim dangling DNS records pointing to deprovisioned AWS services such as Elastic Beanstalk and S3 to intercept traffic." },
      { title: "BAWS 02 – Dumping EBS Secrets", labs: 3, description: "Extract secrets and sensitive data from exposed or misconfigured EBS snapshots by mounting them in a controlled environment.", sample: true, sampleUrl: "https://portal.cloudbreach.io/courses/breaching-aws/lessons/baws-02-dumping-ebs-secrets/topics/baws-02-lab-2/" },
      { title: "BAWS 03 – Enumeration & Command Execution via SSM", labs: 3, description: "Abuse AWS Systems Manager Session Manager to enumerate EC2 instances and execute commands without SSH or open inbound ports." },
      { title: "BAWS 04 – Abuse IAM User Roles, Instance Metadata & SNS", labs: 3, description: "Exploit over-privileged IAM roles and the EC2 Instance Metadata Service (IMDS) to assume roles and abuse SNS for privilege escalation." },
      { title: "BAWS 05 – Capture Credentials from SNS Service", labs: 2, description: "Intercept and capture AWS credentials transmitted through misconfigured SNS topic subscriptions and HTTP endpoints." },
      { title: "BAWS 06 – Get Remote Code Execution on a Lambda Function", labs: 2, description: "Exploit a vulnerable Lambda function to achieve remote code execution and extract environment variables containing secrets." },
      { title: "BAWS 07 – Enumerate and Read Data from DynamoDB", labs: 2, description: "Enumerate accessible DynamoDB tables and exfiltrate records using compromised IAM credentials." },
      { title: "BAWS 08 – Upload Malicious Image into ECR", labs: 2, description: "Push a backdoored container image to Elastic Container Registry and trigger its deployment to gain code execution inside ECS or EKS workloads." },
      { title: "BAWS 09 – AWS SSO Phishing Attack", labs: 2, description: "Execute phishing attacks targeting AWS IAM Identity Center (SSO) to harvest session tokens and gain access to multiple AWS accounts." },
      { title: "BAWS 10 – Enumerate AWS IAM Identity Center Permissions", labs: 2, description: "Map permission sets and account assignments across AWS organizations using IAM Identity Center to identify privilege escalation paths." },
      { title: "BAWS 11 – Enumerate IAM & Retrieve Secrets", labs: 2, description: "Enumerate IAM users, roles, and policies, then extract secrets from AWS Secrets Manager and Systems Manager Parameter Store." },
      { title: "BAWS 12 – Utilising Rancher for Pod Access", labs: 3, description: "Abuse a misconfigured Rancher instance to gain direct access to Kubernetes pods and escalate privileges within the cluster." },
      { title: "BAWS 13 – Enumerate RDS and Retrieve Final Flag", labs: 2, description: "Enumerate RDS database instances, obtain connection credentials, connect to the database, and retrieve the final lab flag." },
    ],
    outcomes: [
      "Enumerate AWS IAM configurations to identify attack paths",
      "Exploit SSRF vulnerabilities to steal IAM credentials from the metadata service",
      "Abuse S3 bucket misconfigurations for data exfiltration",
      "Pivot across AWS accounts using misconfigured AssumeRole policies",
      "Compromise Lambda functions to extract secrets and escalate privileges",
      "Chain vulnerabilities into complete AWS account takeovers",
      "Write professional attack path reports for OAWSP certification",
    ],
  },
  {
    id: "breaching-azure-advanced",
    slug: "breaching-azure-advanced",
    name: "Breaching Azure Advanced",
    shortName: "Breaching Azure Advanced",
    tagline:
      "Beyond the surface. Advanced Azure attack techniques for senior red teamers.",
    description:
      "The advanced course for practitioners who have completed Breaching Azure. Covers multi-tenant attacks, PIM abuse, Conditional Access bypass, Azure DevOps pipeline compromise, and advanced persistence techniques.",
    platform: "Azure",
    level: "Advanced",
    cert: "OASE",
    certFullName: "Offensive Azure Security Expert",
    labCount: 17,
    moduleCount: 17,
    topicCount: 35,
    servicesCount: 23,
    videoCount: 16,
    examAttempts: 1,
    lifetimeAccess: true,
    discord: true,
    color: "#7B2FFF",
    accentColor: "rgba(123,47,255,0.15)",
    logo: "/BreachingAzureAdvanced_logo.png",
    scenario: {
      client: "WindriverTelco",
      logo: "/WindriverTelco_logo.png",
      clientMeta: "Global telecommunications provider · Founded 1970 · Headquartered in New York",
      paragraphs: [
        "WindriverTelco is a global telecommunications provider offering 5G connectivity, ISP services, global SIM and roaming platforms, VoIP, cloud telephony, and enterprise networking.",
        "To accelerate its digital transformation, the board has migrated WindriverTelco's core services and supporting infrastructure to Microsoft Azure. Every lab in the course takes place inside this environment.",
      ],
      objective:
        "Compromise WindriverTelco's Azure environment, starting from an external foothold and working through identity, compute, and supply-chain layers until the tenant is fully breached.",
    },
    pricing: [
      {
        name: "Breaching Azure Advanced (Essential)",
        price: "$599",
        enrollUrl: "https://buy.stripe.com/00weVc4g5dSX6c3aOGeEo0v",
        features: [
          { text: "30 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OASE Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OASE Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
        ],
      },
      {
        name: "Breaching Azure Advanced (Extended)",
        price: "$999",
        enrollUrl: "https://buy.stripe.com/28EdR813T5mrasj6yqeEo0w",
        features: [
          { text: "60 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "1x OASE Exam Attempt", included: true },
          { text: "Certificate of Completion", included: true },
          { text: "PDF Training Guide", included: false },
          { text: "2x OASE Exam Attempts", included: false },
          { text: "Video Lessons", included: false },
        ],
      },
      {
        name: "Breaching Azure Advanced+",
        price: "$1,099",
        badge: "Most Complete",
        featured: true,
        enrollUrl: "https://buy.stripe.com/bJe14m6odaGLgQHe0SeEo0B",
        features: [
          { text: "90 Days Cloud Labs Access", included: true },
          { text: "Lifetime Access to Course Material", included: true },
          { text: "Digital Content", included: true },
          { text: "PDF Training Guide", included: true },
          { text: "2x OASE Exam Attempts", included: true },
          { text: "Video Lessons", included: true },
          { text: "Certificate of Completion", included: true },
        ],
      },
    ],
    faq: [
      {
        question: "What is the exam format?",
        answer: "The exam is practical and based on the advanced course material. You are given access to a live Azure environment for 24 hours and must compromise the target and capture the final flag. You then have 24 hours to submit a high-level report. Passing earns your OASE digital badge.",
      },
      {
        question: "Is there a required format for the exam report?",
        answer: "Yes. Write your report in the CloudBreach exam report template. It lays out the structure we grade against: your attack path, the tools and commands used at each step, evidence screenshots, and a risk analysis with remediation recommendations. Using the template makes sure nothing the graders look for is left out.",
        link: { label: "Open the exam report template", href: EXAM_REPORT_TEMPLATE_URL },
      },
      {
        question: "What level of expertise is required?",
        answer: "At least one year of Microsoft Azure infrastructure experience is recommended. Completing Breaching Azure first is strongly advised. Labs include hints and CloudBreach administrators are available to help.",
      },
      {
        question: "Do I need to install any software?",
        answer: "No. You only need a standard web browser. The training environments are fully hosted on Microsoft Azure cloud and remote access is provided through the browser.",
      },
      {
        question: "Can I enroll today and start later?",
        answer: "Yes. You can purchase the course today and schedule your lab start date for whenever works best for you.",
      },
      {
        question: "Can I pause the lab?",
        answer: "Pausing is not currently possible. The lab runs continuously from your scheduled start date for 30, 60, or 90 days depending on your plan.",
      },
      {
        question: "Can I extend my lab access?",
        answer: "Yes. Lab access can be extended at any time through the CloudBreach portal at cloudbreach.io/addons. Contact an administrator if you need assistance.",
      },
      {
        question: "Can I get a refund?",
        answer: "We do not offer refunds for digital training courses. Please review the course description, objectives, and prerequisites carefully before purchasing.",
      },
    ],
    techniques: [
      {
        name: "Multi-Tenant Attack Chains",
        description:
          "Abuse cross-tenant trust relationships to pivot between Azure AD tenants.",
      },
      {
        name: "PIM Abuse",
        description:
          "Exploit Privileged Identity Management misconfigurations to activate high-privileged roles.",
      },
      {
        name: "Conditional Access Bypass",
        description:
          "Identify and exploit gaps in Conditional Access policies to bypass MFA and location controls.",
      },
      {
        name: "Cross-Tenant Persistence",
        description:
          "Establish persistent access across tenant boundaries using B2B invitations and apps.",
      },
      {
        name: "Azure DevOps Pipeline Attacks",
        description:
          "Compromise CI/CD pipelines to exfiltrate secrets and deploy malicious code.",
      },
      {
        name: "Microsoft Graph API Abuse",
        description:
          "Use the Graph API to enumerate, persist, and escalate privileges stealthily.",
      },
      {
        name: "Application & Service Principal Abuse",
        description:
          "Exploit over-privileged app registrations and service principals for persistence.",
      },
      {
        name: "Azure Arc & Hybrid Attacks",
        description:
          "Target Azure Arc-enabled servers to pivot from cloud to on-premises environments.",
      },
    ],
    modules: [
      { title: "Before You Start", labs: 4, description: "Get your lab environment ready, review the support channels, and understand how to approach the OASE certification exam." },
      { title: "Rules of Engagement", labs: 2, description: "Ethical and legal boundaries for the course labs, plus Command & Control setup used throughout the attack chains." },
      { title: "BAA 00 – Introduction to Azure", labs: 9, description: "Core Azure and Entra ID concepts: managed identities, delegated vs. application permissions, administrative units, Azure Lighthouse, Policy, and Cloud Shell." },
      { title: "BAA 01 – Subdomain Takeover, Teams Phishing & ConsentFix", labs: 6, description: "Claim dangling Azure subdomains, deliver phishing payloads via Microsoft Teams, and exploit OAuth consent flows with the ConsentFix attack." },
      { title: "BAA 02 – Azure VM Metadata Enumeration", labs: 3, description: "Query the Instance Metadata Service (IMDS) from a compromised VM to extract managed identity tokens and escalate access across the subscription." },
      { title: "BAA 03 – Key Vault Access Policies and Secrets", labs: 3, description: "Abuse misconfigured Key Vault access policies to retrieve secrets, keys, and certificates stored for other workloads." },
      { title: "BAA 04 – CosmosDB Data Decryption", labs: 3, description: "Chain Key Vault key access and a Function App to decrypt customer-at-rest data stored inside a CosmosDB instance." },
      { title: "BAA 05 – Entra ID User Creation", labs: 3, description: "Exploit an over-permissioned Azure Function App to create rogue Entra ID users and establish persistence in the tenant." },
      { title: "BAA 06 – Entra ID Enumeration", labs: 3, description: "Map the tenant attack surface using the Microsoft Graph API: users, groups, roles, service principals, and application registrations.", sample: true, sampleUrl: "https://portal.cloudbreach.io/courses/breaching-azure-advanced/lessons/baa-06-entra-id-enumeration/topics/baa-06-lab/" },
      { title: "BAA 07 – Getting RCE on Azure Arc Machine", labs: 3, description: "Exploit Azure Arc-managed servers to achieve remote code execution and pivot from the cloud control plane into on-premises infrastructure." },
      { title: "BAA 08 – RCE on AKS via Container Registry", labs: 3, description: "Push a malicious image to an Azure Container Registry, trigger its deployment in an AKS cluster, and achieve container escape." },
      { title: "BAA 09 – Compromising Entra ID Joined Device", labs: 3, description: "Take over an Entra ID joined device to extract cached tokens, certificates, and credentials usable for lateral movement." },
      { title: "BAA 10 – Exporting PRT and PIM Escalation", labs: 3, description: "Export a Primary Refresh Token (PRT) from a compromised device and abuse Privileged Identity Management (PIM) roles to escalate to Global Admin." },
      { title: "BAA 11 – RCE on Self-Hosted DevOps Agent", labs: 3, description: "Compromise a self-hosted Azure DevOps pipeline agent to execute arbitrary code and exfiltrate secrets injected into pipeline runs." },
      { title: "BAA 12 – ADFS Exploitation", labs: 3, description: "Attack Active Directory Federation Services to forge SAML tokens and authenticate as any federated user without their credentials." },
      { title: "BAA 13 – Extracting Access Token from Browser", labs: 3, description: "Steal Azure access tokens directly from browser memory and session storage to silently impersonate authenticated users." },
      { title: "BAA 14 – Exploiting JWT Assertions", labs: 3, description: "Forge signed JWT client assertions to authenticate as service principals, bypassing secret-based credential requirements entirely." },
      { title: "BAA 15 – Private Endpoints & ARM Template Exposure", labs: 3, description: "Abuse exposed ARM deployment templates and private endpoint misconfigurations to access restricted resources and exfiltrate sensitive data." },
      { title: "BAA 16 – Device Registration via ADFS Certificate", labs: 3, description: "Register a new device using an ADFS-issued certificate to obtain a compliant device identity and bypass Conditional Access policies." },
      { title: "BAA 17 – Exploit Misconfigured Anonymous Storage", labs: 3, description: "Discover and exploit publicly accessible Azure Storage containers to retrieve sensitive data and capture the final lab flag." },
    ],
    outcomes: [
      "Abuse PIM and Conditional Access to bypass modern identity controls",
      "Execute cross-tenant attacks and establish persistent cross-boundary access",
      "Compromise Azure DevOps pipelines and exfiltrate pipeline secrets",
      "Execute advanced lateral movement and persistence across complex enterprise Azure environments",
      "Abuse the Microsoft Graph API for stealthy enumeration and persistence",
      "Build multi-stage attack chains across complex enterprise Azure environments",
      "Write expert-level reports documenting advanced compromise paths for OASE certification",
    ],
  },
];

export function getCourseBySlug(slug: string): Course | undefined {
  return courses.find((c) => c.slug === slug);
}
